import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Calendar,
  Music,
  Ticket,
  DollarSign,
  CheckSquare,
  Users,
  Settings,
  BookOpen,
  Rocket,
  AlertTriangle,
  Bot,
  UserCheck,
  Shield,
  ShoppingBag,
  MessageSquare,
  Lock,
} from 'lucide-react'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Master Plan
   Password-protected internal execution document.
   ═══════════════════════════════════════════════════════════════════════════ */

const MASTER_PLAN_PASSWORD = 'betadizee101'

/* ---------- types ---------- */
type TaskType = 'ai' | 'manual'
type SectionStatus =
  | 'complete'
  | 'almost-complete'
  | 'built-needs-qa'
  | 'needs-enhancement'
  | 'not-built'
  | 'hidden'

interface TaskItem {
  title: string
  description: string
  type: TaskType
}

interface DecisionItem {
  title: string
  description: string
}

interface Section {
  id: string
  number: number
  icon: typeof LayoutDashboard
  title: string
  subtitle: string
  status: SectionStatus
  statusBanner: string
  color: string // tailwind color token e.g. 'sky', 'violet'
  tasks: TaskItem[]
  decisions?: DecisionItem[]
}

/* ---------- status helpers ---------- */
const statusConfig: Record<SectionStatus, { label: string; bg: string; text: string; dot: string }> = {
  complete: { label: 'Complete', bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  'almost-complete': { label: '~Complete', bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
  'built-needs-qa': { label: 'Built — Needs QA', bg: 'bg-sky-500/10', text: 'text-sky-400', dot: 'bg-sky-400' },
  'needs-enhancement': { label: 'Needs Enhancement', bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
  'not-built': { label: 'Not Built', bg: 'bg-rose-500/10', text: 'text-rose-400', dot: 'bg-rose-400' },
  hidden: { label: 'Hidden / Parked', bg: 'bg-zinc-500/10', text: 'text-zinc-500', dot: 'bg-zinc-500' },
}

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string; bannerBg: string }> = {
  sky: {
    border: 'border-sky-500/20',
    bg: 'bg-sky-500/[0.04]',
    text: 'text-sky-400',
    pill: 'bg-sky-500/15 text-sky-300',
    bannerBg: 'bg-sky-500/[0.06] border-sky-500/15',
  },
  violet: {
    border: 'border-violet-500/20',
    bg: 'bg-violet-500/[0.04]',
    text: 'text-violet-400',
    pill: 'bg-violet-500/15 text-violet-300',
    bannerBg: 'bg-violet-500/[0.06] border-violet-500/15',
  },
  emerald: {
    border: 'border-emerald-500/20',
    bg: 'bg-emerald-500/[0.04]',
    text: 'text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-300',
    bannerBg: 'bg-emerald-500/[0.06] border-emerald-500/15',
  },
  amber: {
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/[0.04]',
    text: 'text-amber-400',
    pill: 'bg-amber-500/15 text-amber-300',
    bannerBg: 'bg-amber-500/[0.06] border-amber-500/15',
  },
  rose: {
    border: 'border-rose-500/20',
    bg: 'bg-rose-500/[0.04]',
    text: 'text-rose-400',
    pill: 'bg-rose-500/15 text-rose-300',
    bannerBg: 'bg-rose-500/[0.06] border-rose-500/15',
  },
  zinc: {
    border: 'border-zinc-500/20',
    bg: 'bg-zinc-500/[0.04]',
    text: 'text-zinc-500',
    pill: 'bg-zinc-500/15 text-zinc-400',
    bannerBg: 'bg-zinc-500/[0.06] border-zinc-500/15',
  },
  cyan: {
    border: 'border-cyan-500/20',
    bg: 'bg-cyan-500/[0.04]',
    text: 'text-cyan-400',
    pill: 'bg-cyan-500/15 text-cyan-300',
    bannerBg: 'bg-cyan-500/[0.06] border-cyan-500/15',
  },
  indigo: {
    border: 'border-indigo-500/20',
    bg: 'bg-indigo-500/[0.04]',
    text: 'text-indigo-400',
    pill: 'bg-indigo-500/15 text-indigo-300',
    bannerBg: 'bg-indigo-500/[0.06] border-indigo-500/15',
  },
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION DATA
   ═══════════════════════════════════════════════════════════════════════════ */
const sections: Section[] = [
  /* ── 1 — Overview / Dashboard ─────────────────────────────────────── */
  {
    id: 'overview',
    number: 1,
    icon: LayoutDashboard,
    title: 'Overview / Dashboard',
    subtitle: 'Main landing page after login — shows, music, tasks, schedule at a glance',
    status: 'needs-enhancement',
    statusBanner:
      'The Overview page is fully built and functional. However, dates displayed across the dashboard are off by one day (likely a UTC → local timezone issue). Some layout widgets may be hidden for launch simplification.',
    color: 'sky',
    tasks: [
      {
        title: 'Fix "off-by-one day" date bug globally',
        description:
          'Dates across the dashboard (and the whole app) display one day off. Diagnose whether the issue is in date parsing (new Date() on YYYY-MM-DD strings defaults to UTC midnight, which shifts back in US timezones) or in the backend. Apply a consistent local-date parse utility everywhere.',
        type: 'ai',
      },
      {
        title: 'Hide / clean up overview layout widgets for launch',
        description:
          'Remove or hide any overview layout sections that aren\'t ready for the MVP beta release. Keep it clean: shows summary, upcoming schedule, active tasks, music highlights.',
        type: 'ai',
      },
      {
        title: 'Verify the overview looks correct with real data',
        description:
          'After the date fix is in, load the overview with your real artist data and confirm all numbers, dates, and cards display correctly.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Which overview widgets to keep vs. hide for launch?',
        description:
          'You mentioned you\'d hide some items on the overview layout. Let me know which widgets/cards to hide and I\'ll remove them.',
      },
    ],
  },

  /* ── 2 — Schedule ─────────────────────────────────────────────────── */
  {
    id: 'schedule',
    number: 2,
    icon: Calendar,
    title: 'Schedule',
    subtitle: 'Unified view of shows, tasks, and Google Calendar events',
    status: 'almost-complete',
    statusBanner:
      'The schedule page is fully built with list and calendar views plus Google Calendar integration. Same off-by-one date bug applies here. Calendar view works well with event dots and type badges.',
    color: 'amber',
    tasks: [
      {
        title: 'Fix off-by-one day bug in schedule items',
        description:
          'Same root cause as overview — the date comparison and display logic uses new Date(dateStr) which treats YYYY-MM-DD as UTC. Needs the same local-date parse fix.',
        type: 'ai',
      },
      {
        title: 'Test Google Calendar connect / disconnect flow',
        description:
          'Connect your Google Calendar, verify events sync into the schedule. Then disconnect and verify events clear. Reconnect to make sure the cycle works.',
        type: 'manual',
      },
      {
        title: 'Verify list and calendar views show correct dates',
        description:
          'After date fix, switch between list and calendar view and confirm all events land on the right day.',
        type: 'manual',
      },
    ],
  },

  /* ── 3 — Shows ────────────────────────────────────────────────────── */
  {
    id: 'shows',
    number: 3,
    icon: Ticket,
    title: 'Shows',
    subtitle: 'Canonical record of all live events — CRUD, CSV import, Bandsintown, ticket counter',
    status: 'needs-enhancement',
    statusBanner:
      'Shows is one of the most built-out sections. Manual add, edit, delete, CSV import, Bandsintown sync, ticket counter, status management, search/filter, and bulk operations all work. Key gaps: hover-to-view-details, stream count persistence, and bulk upload credits display.',
    color: 'violet',
    tasks: [
      {
        title: 'Add hover tooltip with show details',
        description:
          'When hovering over a show row in the table, display a popover/tooltip card showing full details: date, venue, city, artist, guarantee, ticket counts, status, and any notes.',
        type: 'ai',
      },
      {
        title: 'Fix stream count persistence (not staying saved)',
        description:
          'The stream/ticket count entered via the Ticket Counter isn\'t persisting properly — it resets or disappears after page reload. Investigate whether the API is storing the value and whether the frontend re-reads it correctly.',
        type: 'ai',
      },
      {
        title: 'Diagnose and fix bulk upload (CSV) issue',
        description:
          'Deep-dive into the CSV bulk upload flow — some users report rows not importing or credits not showing. Test with various CSV formats (PXS, Eventbrite, generic) and fix parsing edge cases.',
        type: 'ai',
      },
      {
        title: 'Show credits on imported shows',
        description:
          'After CSV or Bandsintown import, ensure that show credits (guarantee, capacity, etc.) are visible and persisted, not just the basic date/venue/city.',
        type: 'ai',
      },
      {
        title: 'Test manual add → edit → delete flow end-to-end',
        description:
          'Create a show manually, edit it via the three-dots menu, update fields, save. Then delete it. Verify all changes persist.',
        type: 'manual',
      },
      {
        title: 'Test CSV and Bandsintown import with real data',
        description:
          'Upload a real CSV export from your agent/promoter and import via Bandsintown. Verify all rows come in with correct dates, venues, cities, and statuses.',
        type: 'manual',
      },
    ],
  },

  /* ── 4 — Music ────────────────────────────────────────────────────── */
  {
    id: 'music',
    number: 4,
    icon: Music,
    title: 'Music',
    subtitle: 'Catalog management, streaming stats, and release data',
    status: 'needs-enhancement',
    statusBanner:
      'Music page is built with catalog display, streaming data via Soundcharts API, and release management. Key issues: stream count not persisting between sessions, bulk upload not showing credits, and automatic catalog pulling for new users on onboard.',
    color: 'cyan',
    tasks: [
      {
        title: 'Make stream count persistent across sessions',
        description:
          'Stream counts currently reset or don\'t carry over between sessions. Ensure the backend stores the counts and the frontend reads from API on load rather than relying solely on session cache.',
        type: 'ai',
      },
      {
        title: 'Fix bulk upload showing credits',
        description:
          'When bulk uploading releases/tracks, the credits (writers, producers, features) aren\'t displaying in the UI afterward. Fix the data mapping from the upload response to the display layer.',
        type: 'ai',
      },
      {
        title: 'Add automatic catalog pull on first sign-up / onboard',
        description:
          'When a user first signs up and onboards, automatically pull their catalog (from Soundcharts or the configured source) so they don\'t start with an empty music section.',
        type: 'ai',
      },
      {
        title: 'Test streaming stats with Soundcharts API key',
        description:
          'Configure a Soundcharts API key in Settings, navigate to Music, and verify streaming numbers pull in correctly and persist.',
        type: 'manual',
      },
      {
        title: 'Test bulk upload with a real release sheet',
        description:
          'Upload a real set of releases and verify all metadata (credits, artwork, dates) comes through properly.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Which catalog source to auto-pull from on onboard?',
        description:
          'Should the auto-pull use Soundcharts, Spotify API, or another source? This determines what API key is required during onboarding.',
      },
    ],
  },

  /* ── 5 — Tasks ────────────────────────────────────────────────────── */
  {
    id: 'tasks',
    number: 5,
    icon: CheckSquare,
    title: 'Tasks',
    subtitle: 'Task management and team assignments',
    status: 'needs-enhancement',
    statusBanner:
      'Tasks page is built with create functionality and a table view. However, there\'s no way to click into a task to edit/delete it — the three-dots menu (like Shows and Music have) is missing. Need to add edit/delete actions to each task row.',
    color: 'sky',
    tasks: [
      {
        title: 'Add three-dots menu to each task row (edit + delete)',
        description:
          'Add the same three-dots dropdown pattern used on Shows and Finance to each task row. Include "Edit" (opens the task in a modal with pre-filled fields) and "Delete" (with confirmation). Also add an inline status toggle (pending → in-progress → completed).',
        type: 'ai',
      },
      {
        title: 'Add task edit modal with pre-filled data',
        description:
          'When clicking "Edit" from the three-dots menu, open the create task modal but pre-populate all fields with the existing task data. On save, call PUT to update.',
        type: 'ai',
      },
      {
        title: 'Test create → edit → complete → delete flow',
        description:
          'Create a task, edit it, mark it complete, then delete it. Verify all state changes persist after page reload.',
        type: 'manual',
      },
    ],
  },

  /* ── 6 — Team Management ──────────────────────────────────────────── */
  {
    id: 'team',
    number: 6,
    icon: Users,
    title: 'Team Management',
    subtitle: 'Invite, permissions, roles, and team member access control',
    status: 'built-needs-qa',
    statusBanner:
      'Team management is fully built: invite flow, role selection (Manager / Support), granular section permissions, edit permissions, remove member, cancel invitation. Needs thorough QA to verify the entire gating flow works correctly end-to-end.',
    color: 'violet',
    tasks: [
      {
        title: 'Test invite flow: Manager with full permissions',
        description:
          'As the artist, invite a new Manager with all permissions. Have them accept the invite, log in, and verify they see all sections.',
        type: 'manual',
      },
      {
        title: 'Test invite flow: Support with limited permissions',
        description:
          'Invite a Support role with only 2-3 sections enabled. Verify they can only see those sections and the rest are gated.',
        type: 'manual',
      },
      {
        title: 'Test permission editing after invite',
        description:
          'After a team member is active, use "Edit Permissions" to change their section access. Verify the changes take effect immediately (they should lose/gain sidebar items).',
        type: 'manual',
      },
      {
        title: 'Test remove member and cancel invitation',
        description:
          'Remove an active team member and verify they lose access. Send a new invite and cancel it before acceptance.',
        type: 'manual',
      },
      {
        title: 'Verify gating works granularly per section',
        description:
          'With a limited-permission team member logged in, try directly navigating to a restricted route (e.g. /finance). It should redirect or show access denied.',
        type: 'manual',
      },
    ],
  },

  /* ── 7 — Settings ─────────────────────────────────────────────────── */
  {
    id: 'settings',
    number: 7,
    icon: Settings,
    title: 'Settings',
    subtitle: 'Profile, API keys, Google Calendar, Google Sheets, accessibility',
    status: 'needs-enhancement',
    statusBanner:
      'Settings page has profile editing (for team members), Google Calendar connect, API key management (Soundcharts, Shopify), Google Sheets sync, and accessibility settings. Missing: profile picture and name/email update for the main artist account. Team account profile editing also needs fixing.',
    color: 'emerald',
    tasks: [
      {
        title: 'Add profile settings for the main artist account',
        description:
          'The "Personal Settings" section (avatar upload, name, email) currently only shows for team members (isTeamMember check). Add a parallel section that shows for the main artist account so they can update their avatar, display name, and email.',
        type: 'ai',
      },
      {
        title: 'Fix profile picture upload for team accounts',
        description:
          'Team members can see the profile section but the avatar upload and name/email save may not be hitting the correct endpoint. Verify the upload flow works and the API correctly updates the team member\'s profile.',
        type: 'ai',
      },
      {
        title: 'Test profile update on artist account',
        description:
          'As the main artist, upload a profile picture and change name/email. Verify changes persist after refresh and appear in the sidebar avatar.',
        type: 'manual',
      },
      {
        title: 'Test profile update on team member account',
        description:
          'As a team member (manager/support), update profile picture and name. Verify the changes save correctly.',
        type: 'manual',
      },
    ],
  },

  /* ── 8 — Finance ──────────────────────────────────────────────────── */
  {
    id: 'finance',
    number: 8,
    icon: DollarSign,
    title: 'Finance',
    subtitle: 'Revenue, spend, weekly/monthly/YTD summaries, entry management',
    status: 'built-needs-qa',
    statusBanner:
      'Finance is fully built with CRUD, paid/unpaid toggling, search/filter, bulk delete, and summary stats (week/month/YTD). Also has Google Sheets sync integration in Settings. Needs QA testing and Google Sheets setup for each section.',
    color: 'emerald',
    tasks: [
      {
        title: 'Test add → edit → delete financial entry flow',
        description:
          'Create a revenue entry and a spend entry. Edit both. Toggle paid status. Delete one. Verify everything persists.',
        type: 'manual',
      },
      {
        title: 'Verify summary stats (week / month / YTD) are correct',
        description:
          'Add entries across different dates and check that the This Week, This Month, and YTD cards show the correct totals.',
        type: 'manual',
      },
      {
        title: 'Set up Google Sheets sync for Finance',
        description:
          'In Settings → Google Sheets, configure the workspace ID and secret token. Copy the Dizee template sheet and connect it. Test bidirectional sync.',
        type: 'manual',
      },
      {
        title: 'Set up Google Sheets for each releasing section',
        description:
          'Create and configure a Google Sheet for each section (Shows, Music, Finance) so data flows both ways. Test that changes in the app sync to the sheet and vice versa.',
        type: 'manual',
      },
    ],
  },

  /* ── 9 — Knowledge Base ───────────────────────────────────────────── */
  {
    id: 'knowledge-base',
    number: 9,
    icon: BookOpen,
    title: 'Knowledge Base',
    subtitle: 'Documentation hub ported from the legacy app',
    status: 'not-built',
    statusBanner:
      'The legacy app had a full knowledge base (knowledgeBaseData.js) with help articles, guides, and FAQs. This has not been ported to the new app. It could live as an in-app section, a separate docs page, or a link to an external docs site.',
    color: 'rose',
    tasks: [
      {
        title: 'Port legacy knowledge base content to new app',
        description:
          'Extract content from legacy/src/utils/knowledgeBaseData.js and create a new KnowledgeBasePage or docs-style page. Could be a searchable accordion layout or a clean docs-style page with sidebar navigation.',
        type: 'ai',
      },
      {
        title: 'Review ported knowledge base content for accuracy',
        description:
          'After the content is ported, review all articles to make sure they reflect the new app\'s UI and features. Remove outdated references.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Where should the knowledge base live?',
        description:
          'Options: (A) In-app page accessible from the sidebar, (B) Separate /docs route in a docs-style layout, (C) External link that opens in a new tab (e.g. Notion or GitBook). Your call on what feels right for the MVP.',
      },
    ],
  },

  /* ── 10 — Onboarding / Signup ─────────────────────────────────────── */
  {
    id: 'onboarding',
    number: 10,
    icon: Rocket,
    title: 'Onboarding / Signup',
    subtitle: '8-step onboarding flow: OTP, presence, username, releases, avatar, team',
    status: 'almost-complete',
    statusBanner:
      'The 8-step onboarding flow is built and functional (OTP send/verify, find presence, set username, add releases, avatar upload, team join/create). The main enhancement needed is automatic catalog pulling when a user completes onboarding.',
    color: 'amber',
    tasks: [
      {
        title: 'Add auto-catalog pull after onboarding completes',
        description:
          'After the user finishes the last onboarding step, trigger an automatic catalog pull (Soundcharts or configured source) so they land on a populated Music page instead of an empty one.',
        type: 'ai',
      },
      {
        title: 'Test full onboarding flow end-to-end',
        description:
          'Create a fresh account and go through all 8 steps. Verify OTP, find presence, set username, add releases, upload avatar, and team join/create all work.',
        type: 'manual',
      },
    ],
  },

  /* ── 11 — Commerce (Hidden) ───────────────────────────────────────── */
  {
    id: 'commerce',
    number: 11,
    icon: ShoppingBag,
    title: 'Commerce / Fashion',
    subtitle: 'Shopify integration and order management — currently hidden from sidebar',
    status: 'hidden',
    statusBanner:
      'Commerce page is built with Shopify order display and filtering. It\'s currently hidden from the sidebar for the MVP beta. Can be re-enabled when ready.',
    color: 'zinc',
    tasks: [
      {
        title: 'Verify Commerce page still works with Shopify data',
        description:
          'Unhide the Commerce sidebar item, navigate to it, and make sure it still pulls in Shopify data correctly.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Should Commerce be included in this MVP release?',
        description:
          'It\'s currently hidden in the sidebar. If you want it in the beta, I\'ll unhide it. If not, we leave it hidden.',
      },
    ],
  },

  /* ── 12 — Chat (Hidden) ───────────────────────────────────────────── */
  {
    id: 'chat',
    number: 12,
    icon: MessageSquare,
    title: 'Chat / AI Assistant',
    subtitle: 'AI chat with voice, file upload, and prompt suggestions — currently hidden',
    status: 'hidden',
    statusBanner:
      'Chat page is built with AI messaging, voice recording, file upload, and default prompt suggestions. Currently hidden from the sidebar. Can be re-enabled when the backend AI integration is stable.',
    color: 'zinc',
    tasks: [
      {
        title: 'Test chat functionality with backend AI',
        description:
          'Unhide the Chat sidebar item and test sending messages, using voice, uploading files, and selecting default prompts.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Should Chat be included in this MVP release?',
        description:
          'It\'s currently hidden. Decide if it should go live in the beta or stay hidden until the AI backend is production-ready.',
      },
    ],
  },

  /* ── 13 — Auth & Security ─────────────────────────────────────────── */
  {
    id: 'auth',
    number: 13,
    icon: Shield,
    title: 'Auth & Security',
    subtitle: 'Login, OTP, protect page, session management',
    status: 'almost-complete',
    statusBanner:
      'Auth flow is built: protect page (beta gate), login with email, OTP verification, session tokens, and protected routes. The gating system for team permissions is also wired through ProtectedRoute. Needs a full QA pass.',
    color: 'amber',
    tasks: [
      {
        title: 'Test login → OTP → dashboard flow',
        description:
          'Log out, go to protect page, enter beta password, login with email, receive OTP, verify, and confirm you land on the dashboard.',
        type: 'manual',
      },
      {
        title: 'Test session persistence and expiry',
        description:
          'Log in, close the browser, reopen. Verify the session is still active. Then clear localStorage and verify you\'re redirected to login.',
        type: 'manual',
      },
    ],
  },
]

/* ═══════════════════════════════════════════════════════════════════════════
   EXECUTION ORDER
   ═══════════════════════════════════════════════════════════════════════════ */
interface Phase {
  number: number
  title: string
  subtitle: string
  color: string
  items: string[]
}

const phases: Phase[] = [
  {
    number: 1,
    title: 'Quick Wins',
    subtitle: 'High-impact fixes that unblock everything else',
    color: 'emerald',
    items: [
      'Fix the global off-by-one day date bug (Overview, Schedule, Shows)',
      'Add three-dots menu to Tasks (edit + delete)',
      'Add profile settings for the main artist account in Settings',
      'Fix profile picture upload for team member accounts',
    ],
  },
  {
    number: 2,
    title: 'Feature Additions',
    subtitle: 'New capabilities that complete the MVP feature set',
    color: 'sky',
    items: [
      'Add hover tooltip for show details on Shows page',
      'Fix stream count persistence in Music',
      'Fix bulk upload credits display in Music',
      'Port legacy knowledge base to new app',
      'Add auto-catalog pull after onboarding',
    ],
  },
  {
    number: 3,
    title: 'Core Product Hardening',
    subtitle: 'Make existing features rock-solid',
    color: 'violet',
    items: [
      'Deep-dive CSV bulk upload issue in Shows',
      'Fix show credits display after import',
      'Ensure stream count persistence across sessions in Music',
      'Set up Google Sheets sync for Shows, Music, Finance',
      'Hide/clean up overview layout widgets for launch',
    ],
  },
  {
    number: 4,
    title: 'Full QA',
    subtitle: 'Manual testing of every flow before launch',
    color: 'amber',
    items: [
      'Test Team Management: invite, permissions, gating, edit, remove',
      'Test Finance: CRUD, summaries, paid toggle, Google Sheets',
      'Test Auth: login, OTP, session, protected routes',
      'Test Schedule: Google Calendar connect/disconnect, views',
      'Test Onboarding: full 8-step flow end-to-end',
      'Test Shows: manual add, edit, delete, CSV, Bandsintown',
      'Test Settings: all sections, all account types',
    ],
  },
]

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function MasterPlanPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === MASTER_PLAN_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenSections(new Set(sections.map((s) => s.id)))
  const collapseAll = () => setOpenSections(new Set())

  /* ---------- stats ---------- */
  const totalSections = sections.length
  const aiBuildTasks = sections.reduce((sum, s) => sum + s.tasks.filter((t) => t.type === 'ai').length, 0)
  const manualTestTasks = sections.reduce((sum, s) => sum + s.tasks.filter((t) => t.type === 'manual').length, 0)
  const totalDecisions = sections.reduce((sum, s) => sum + (s.decisions?.length || 0), 0)

  /* ── Password Gate ─────────────────────────────────────────────────── */
  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8 bg-zinc-950">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">Master Plan</span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view the execution plan.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
              />
              {error && (
                <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
            >
              View Plan
            </button>
          </form>
        </div>
      </div>
    )
  }

  /* ── Main Content ──────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Confidential watermark */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.015] rotate-[-25deg]">
          <span className="text-[180px] font-bold tracking-widest text-white whitespace-nowrap select-none">
            CONFIDENTIAL
          </span>
        </div>
      </div>

      {/* ── Sticky Header ──────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full">
              Master Plan
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-12">
        {/* ── Quick Nav Pills ──────────────────────────────────────────── */}
        <nav className="flex flex-wrap gap-2">
          {sections.map((s) => {
            const c = colorMap[s.color] || colorMap.sky
            return (
              <button
                key={s.id}
                onClick={() => {
                  if (!openSections.has(s.id)) toggleSection(s.id)
                  document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all hover:scale-[1.03] ${c.border} ${c.bg} ${c.text}`}
              >
                {s.number}. {s.title}
              </button>
            )
          })}
        </nav>

        {/* ── Progress Overview Cards ──────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Sections', value: totalSections, color: 'text-white' },
            { label: 'AI-Buildable Tasks', value: aiBuildTasks, color: 'text-sky-400' },
            { label: 'Manual Tests', value: manualTestTasks, color: 'text-violet-400' },
            { label: 'Decisions Needed', value: totalDecisions, color: 'text-amber-400' },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5"
            >
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">{card.label}</div>
              <div className={`text-2xl font-semibold ${card.color}`}>{card.value}</div>
            </div>
          ))}
        </div>

        {/* ── Expand / Collapse All ────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <button
            onClick={expandAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Expand All
          </button>
          <span className="text-zinc-700">|</span>
          <button
            onClick={collapseAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Collapse All
          </button>
        </div>

        {/* ── Sections ─────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {sections.map((section) => {
            const isOpen = openSections.has(section.id)
            const c = colorMap[section.color] || colorMap.sky
            const st = statusConfig[section.status]
            const Icon = section.icon

            return (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={`rounded-xl border ${c.border} ${c.bg} overflow-hidden transition-all scroll-mt-24`}
              >
                {/* Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-4 px-6 py-5 text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.pill}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-zinc-600 font-medium">#{section.number}</span>
                      <h2 className="text-[15px] font-semibold text-white truncate">{section.title}</h2>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${st.bg} ${st.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                        {st.label}
                      </span>
                    </div>
                    <p className="text-[12px] text-zinc-500 mt-1 truncate">{section.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>

                {/* Body */}
                {isOpen && (
                  <div className="px-6 pb-6 space-y-5">
                    {/* Status Banner */}
                    <div className={`rounded-lg border p-4 ${c.bannerBg}`}>
                      <p className="text-[12px] text-zinc-400 leading-relaxed">{section.statusBanner}</p>
                    </div>

                    {/* Decision Callouts */}
                    {section.decisions && section.decisions.length > 0 && (
                      <div className="space-y-3">
                        {section.decisions.map((d, i) => (
                          <div
                            key={i}
                            className="rounded-lg border border-amber-500/20 bg-amber-500/[0.06] p-4"
                          >
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="text-[13px] font-semibold text-amber-300">{d.title}</h4>
                                <p className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{d.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Task Items */}
                    <div className="space-y-3">
                      {/* AI tasks */}
                      {section.tasks.filter((t) => t.type === 'ai').length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Bot className="w-3.5 h-3.5 text-sky-400" />
                            <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                              Things I Can Build
                            </span>
                          </div>
                          <div className="space-y-2">
                            {section.tasks
                              .filter((t) => t.type === 'ai')
                              .map((task, i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3"
                                >
                                  <Bot className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-[13px] font-medium text-white">{task.title}</h4>
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-500/15 text-sky-300">
                                        AI Build
                                      </span>
                                    </div>
                                    <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed">{task.description}</p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Manual tasks */}
                      {section.tasks.filter((t) => t.type === 'manual').length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3 mt-4">
                            <UserCheck className="w-3.5 h-3.5 text-violet-400" />
                            <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                              Things You Need to Test
                            </span>
                          </div>
                          <div className="space-y-2">
                            {section.tasks
                              .filter((t) => t.type === 'manual')
                              .map((task, i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3"
                                >
                                  <UserCheck className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-[13px] font-medium text-white">{task.title}</h4>
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-500/15 text-violet-300">
                                        Your Test
                                      </span>
                                    </div>
                                    <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed">{task.description}</p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ── Recommended Execution Order ──────────────────────────────── */}
        <div className="pt-8">
          <h2 className="text-lg font-semibold text-white mb-2">Recommended Execution Order</h2>
          <p className="text-[12px] text-zinc-500 mb-8">
            Work through these phases in order. Quick wins first to unblock everything, then features, then hardening, then full QA.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {phases.map((phase) => {
              const c = colorMap[phase.color] || colorMap.sky
              return (
                <div
                  key={phase.number}
                  className={`rounded-xl border ${c.border} ${c.bg} p-6`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${c.pill}`}>
                      {phase.number}
                    </span>
                    <div>
                      <h3 className="text-[14px] font-semibold text-white">{phase.title}</h3>
                      <p className="text-[11px] text-zinc-500">{phase.subtitle}</p>
                    </div>
                  </div>
                  <ul className="space-y-2.5">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${c.text.replace('text-', 'bg-')}`} />
                        <span className="text-[12px] text-zinc-400 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>
      </main>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/[0.06] mt-16">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <span className="text-[11px] text-zinc-600">Confidential — Internal Use Only</span>
          <span className="text-[11px] text-zinc-700">Last updated: February 8, 2026</span>
        </div>
      </footer>
    </div>
  )
}
